import { PlusOutlined } from '@ant-design/icons';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button } from 'antd';
import React, { useEffect, useMemo, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import ValuesListSortable from './ValuesListSortable';

const ValuesList = ({ value, onChange }) => {
  // value shape: { elements: [...] }
  // elements: [{ type: 'attribute'|'freetext'|'media', value: '...', 'media_type': 'image'|'video'|'document', 'image_order': number, id: '...' }]

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Track previous fields to detect when new data is loaded
  const previousElementsRef = useRef(value?.elements);

  // Normalize items: ensure each field has an id for drag and drop
  // When data is loaded from backend, fields may not have ids
  const items = useMemo(() => {
    const elements = value?.elements || [];
    return elements.map((element) => {
      // If element already has an id, keep it; otherwise generate one
      if (element.id) {
        return element;
      }
      return {
        ...element,
        id: uuidv4(),
      };
    });
  }, [value?.elements]);

  // TODO: optim this with useMemo/lodash
  // Sync normalized data back to parent if IDs were added
  // This ensures IDs are persisted when data is loaded from backend
  useEffect(() => {
    const currentElements = value?.elements || [];
    const previousElements = previousElementsRef.current || [];

    // Check if fields have actually changed (new data loaded)
    const elementsChanged =
      currentElements.length !== previousElements.length ||
      currentElements.some(
        (element, index) =>
          !previousElements[index] ||
          element.type !== previousElements[index]?.type ||
          element.value !== previousElements[index]?.value ||
          !element.id // Also trigger if current element is missing ID
      );

    // If fields changed and some are missing IDs, normalize and sync
    if (elementsChanged) {
      const hasMissingIds = currentElements.some((element) => !element.id);

      if (hasMissingIds) {
        const normalizedElements = currentElements.map((element) => {
          if (element.id) {
            return element;
          }
          return {
            ...element,
            id: uuidv4(),
          };
        });

        // Update parent with normalized elements
        onChange({
          ...value,
          elements: normalizedElements,
        });
      }
    }

    previousElementsRef.current = currentElements;
  }, [value?.elements]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return; // Invalid indices, skip update
      }

      const newItems = arrayMove(items, oldIndex, newIndex);
      onChange({
        ...value,
        elements: newItems,
      });
    }
  };

  const handleAddItem = () => {
    const newItem = { id: uuidv4(), type: 'attribute', value: '' };
    const newElements = [...items, newItem];
    onChange({
      ...value,
      elements: newElements,
    });
  };

  const handleDeleteItem = (id) => {
    const newElements = items.filter((item) => item.id !== id);
    onChange({
      ...value,
      elements: newElements,
    });
  };

  const handleTypeChange = (id, newType) => {
    const newElements = items.map((item) =>
      item.id === id ? { ...item, type: newType, value: '' } : item
    );
    onChange({
      ...value,
      elements: newElements,
    });
  };

  const handleValueChange = (id, newValue) => {
    const newElements = items.map((item) =>
      item.id === id ? { ...item, value: newValue } : item
    );
    onChange({
      ...value,
      elements: newElements,
    });
  };

  return (
    <div>
      <div style={{ margin: '8px 0' }}>
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          Éléments de la liste
        </span>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((item) => (
              <ValuesListSortable
                key={item.id}
                id={item.id}
                item={item}
                onTypeChange={handleTypeChange}
                onValueChange={handleValueChange}
                onDelete={handleDeleteItem}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button
        type="dashed"
        onClick={handleAddItem}
        style={{ width: '100%', marginTop: 16 }}
        icon={<PlusOutlined />}
      >
        Ajouter un élément
      </Button>
    </div>
  );
};

export default ValuesList;
